import { Providers, VoiceConfig } from '@d-id/types';

// Providers that support speed/rate control
export const PROVIDERS_WITH_SPEED_CONTROL: Providers[] = [Providers.Elevenlabs, Providers.Microsoft];

/**
 * Check if a provider supports speed/rate control
 */
export function isProviderSupportsSpeedControl(provider: Providers | null | undefined): boolean {
    return !!provider && PROVIDERS_WITH_SPEED_CONTROL.includes(provider);
}

// Speed control ranges for different TTS providers
export const SPEED_LIMITS = {
    ELEVENLABS: {
        MIN: 0.7,
        MAX: 1.2,
    },
    DEFAULT: {
        MIN: 0.5,
        MAX: 1.5,
    },
} as const;

/**
 * Get the speed limits for a given provider
 */
export function getSpeedLimits(provider: Providers) {
    const isElevenLabs = provider === Providers.Elevenlabs;
    return {
        min: isElevenLabs ? SPEED_LIMITS.ELEVENLABS.MIN : SPEED_LIMITS.DEFAULT.MIN,
        max: isElevenLabs ? SPEED_LIMITS.ELEVENLABS.MAX : SPEED_LIMITS.DEFAULT.MAX,
    };
}

/**
 * Constrain a speed value to the valid range for a provider
 */
export function constrainSpeed(speed: number, provider: Providers): number {
    const { min, max } = getSpeedLimits(provider);

    if (speed < min) {
        return min;
    }
    if (speed > max) {
        return max;
    }
    return speed;
}

/**
 * Get a valid voiceConfig with speed constrained to provider's range
 * Only applies to providers that support speed control
 */
export function getValidSpeed(voiceConfig: VoiceConfig, provider: Providers): VoiceConfig {
    // Only these providers support speed/rate control
    const supportsRate = isProviderSupportsSpeedControl(provider);

    if (!supportsRate) {
        return voiceConfig;
    }

    const rate = voiceConfig.rate ? parseFloat(voiceConfig.rate) : 1;
    const constrainedRate = constrainSpeed(rate, provider);

    return {
        ...voiceConfig,
        rate: constrainedRate.toString(),
    };
}
